import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Plus } from "lucide-react";
import type { SpendingGoalWithProgress } from "@repo/share-log-interfaces";
import { Button } from "@web/components/ui/button";
import { Spinner } from "@web/components/ui/spinner";
import { LoadingOverlay } from "@web/components/ui/loading";
import { useSpendingGoalStore, useAuthStore, useWorkspaceStore, useCategoryStore, useMethodStore } from "@web/stores";
import { SpendingGoalList } from "@web/features/spending-goal/components/spending-goal-list";
import { SpendingGoalFormDialog } from "@web/features/spending-goal/components/spending-goal-form-dialog";
import { modalService } from "@web/features/modal";

export function SpendingGoalsPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const workspaceIdNum = workspaceId ? parseInt(workspaceId, 10) : 0;

  const { goals, fetchGoals, deleteGoal, toggleActive, status, hasMore, loadMore } =
    useSpendingGoalStore();
  const { user } = useAuthStore();
  const { members, fetchMembers } = useWorkspaceStore();
  const { categories, fetchCategories } = useCategoryStore();
  const { methods, fetchMethods } = useMethodStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<
    SpendingGoalWithProgress | undefined
  >(undefined);

  useEffect(() => {
    if (workspaceIdNum) {
      void fetchGoals(workspaceIdNum);
      void fetchMembers(workspaceIdNum);
      void fetchCategories(workspaceIdNum);
      void fetchMethods(workspaceIdNum);
    }
  }, [workspaceIdNum, fetchGoals, fetchMembers, fetchCategories, fetchMethods]);

  const handleCreate = () => {
    setEditingGoal(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (goal: SpendingGoalWithProgress) => {
    setEditingGoal(goal);
    setDialogOpen(true);
  };

  const handleDelete = async (goalId: number) => {
    const confirmed = await modalService.destructive("정말 삭제하시겠습니까?");
    if (!confirmed) return;
    await deleteGoal(workspaceIdNum, goalId);
  };

  const handleToggleActive = async (goalId: number, isActive: boolean) => {
    await toggleActive(workspaceIdNum, goalId, isActive);
  };

  const handleLoadMore = () => {
    void loadMore(workspaceIdNum);
  };

  if (status === "loading" && goals.length === 0) {
    return <LoadingOverlay />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">지출 목표</h1>
          <p className="text-muted-foreground">
            월별 지출 목표를 설정하고 관리하세요
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />새 목표
        </Button>
      </div>

      {/* List */}
      <SpendingGoalList
        goals={goals}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
        currentUserId={user?.id ?? 0}
        members={members}
        categories={categories}
        methods={methods}
      />

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={status === "loading"}
          >
            {status === "loading" ? (
              <>
                <Spinner size="sm" className="mr-2" />
                로딩 중...
              </>
            ) : (
              "더 보기"
            )}
          </Button>
        </div>
      )}

      {/* Form Dialog */}
      <SpendingGoalFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        goal={editingGoal}
        workspaceId={workspaceIdNum}
        members={members}
        categories={categories}
        methods={methods}
        currentUserId={user?.id ?? 0}
      />
    </div>
  );
}

